import { Layout } from "@/components/layout/Layout";
import { HeroSection } from "@/components/home/HeroSection";
import { FeaturedBlogSection } from "@/components/home/FeaturedBlogSection";
import { PortfolioGrid } from "@/components/portfolio/PortfolioGrid";
import { SEO } from "@/components/SEO";
import { m as motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { HtmlContent } from "@/components/ui/html-content";
import { getCollectionPageSchema } from "@/lib/seo-utils";

const Index = () => {
  const { homeSettings } = useSiteSettings();

  const stats = homeSettings.stats || [
    { number: "100+", label: "Projects Completed" },
    { number: "50+", label: "Clients Served" },
    { number: "10+", label: "Years Experience" },
    { number: "100k+", label: "Learners Reached" },
  ];

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Nathan Stoker Portfolio",
    url: "https://natestoker.com",
    description: "Creating engaging eLearning experiences, custom SharePoint solutions, and compelling visual content.",
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Nathan Stoker",
    url: "https://natestoker.com",
    logo: "https://natestoker.com/og-image.webp",
    description: "Security Multimedia & Instructional Design Lead creating engaging eLearning experiences, custom SharePoint solutions, and compelling visual content.",
    sameAs: [],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      url: "https://natestoker.com/about#contact",
    },
  };

  return (
    <Layout>
      <SEO 
        structuredData={[websiteSchema, organizationSchema]}
      />
      
      {/* Hero Section */}
      <HeroSection />

      {/* Portfolio Section */}
      <section id="portfolio" className="py-16 md:py-24 bg-card">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              {homeSettings.featuredWorkTitle || "Featured Work"}
            </h2>
            <HtmlContent 
              content={homeSettings.featuredWorkDescription || "A selection of projects showcasing my expertise in eLearning development, multimedia design, and custom application building."}
              className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto [&_p]:m-0"
            />
          </motion.div>

          <PortfolioGrid showFilter={false} featuredOnly={true} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button asChild size="lg" variant="outline">
              <Link to="/portfolio">
                View All Portfolio <ArrowRight size={18} className="ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Featured Blog Section */}
      <FeaturedBlogSection />

      {/* Skills Preview Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                {homeSettings.skillsPreviewTitle || "Building Learning Experiences That Stick"}
              </h2>
              <HtmlContent 
                content={homeSettings.skillsPreviewDescription1 || "With over a decade of experience in instructional design and multimedia development, I specialize in creating engaging content that transforms how organizations train their teams."}
                className="mt-6 text-lg text-muted-foreground [&_p]:m-0"
              />
              <HtmlContent 
                content={homeSettings.skillsPreviewDescription2 || "From interactive eLearning modules to custom SharePoint applications, I bring technical expertise and creative vision to every project."}
                className="mt-4 text-lg text-muted-foreground [&_p]:m-0"
              />
              <Button asChild className="mt-8" size="lg">
                <Link to={homeSettings.skillsPreviewButtonLink || "/about"}>{homeSettings.skillsPreviewButtonText || "Learn More About Me"}</Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="p-6 rounded-xl bg-card border border-border text-center"
                >
                  <div className="text-3xl md:text-4xl font-bold text-primary">
                    {stat.number}
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              {homeSettings.ctaSectionTitle || "Ready to Transform Your Training?"}
            </h2>
            <HtmlContent 
              content={homeSettings.ctaSectionDescription || "Let's discuss how we can create engaging learning experiences for your organization."}
              className="mt-4 text-lg text-muted-foreground [&_p]:m-0"
            />
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <Link to={homeSettings.ctaSectionPrimaryLink || "/about#contact"}>
                  {homeSettings.ctaSectionPrimaryText || "Start a Conversation"}
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to={homeSettings.ctaSectionSecondaryLink || "/blog"}>
                  {homeSettings.ctaSectionSecondaryText || "Read the Blog"}
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;